/* __next_internal_client_entry_do_not_use__ createContextStore auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import React, { createContext, useContext, useEffect } from "react";
import { useSyncExternalStore } from "use-sync-external-store/shim";
import { ALL_FIELDS_MARKER } from "./types";
import { useStoreMetadata } from "./useStoreMetadata";
/**
 * Create a Context Store to manage frontend state. A Context Store is an object that contains
 *   - a useStore hook that's used to consume state in the context
 *   - a propless Provider component that provides store subscription data to useStore consumers.
 *
 * More info: https://dev.faire.team/frontend/pattern/ContextStores
 *
 * @param initialStore the initial store state object
 * @returns a ContextStoreType object containing the Provider component and the useStore hook.
 */ export var createContextStore = function(initialStore, options) {
    var useStore = function useStore(fields) {
        var store = useContext(StoreMetadataContext);
        useEffect(function() {
            var _process_env, _process;
            if (!store && getWindowOrThrow().location.hostname.includes("localhost") && ((_process = process) === null || _process === void 0 ? void 0 : (_process_env = _process.env) === null || _process_env === void 0 ? void 0 : _process_env.NODE_ENV) !== "test") {
                var name = (options === null || options === void 0 ? void 0 : options.name) || "ContextStore";
                var fieldsArrayString = fields !== ALL_FIELDS_MARKER ? '["'.concat(fields.join('", "'), '"]') : "ALL_FIELDS";
                // Warn if the consumer is accessing the store without a Provider higher up in the tree
                // eslint-disable-next-line no-console
                console.warn("".concat(name, " consumer of fields ").concat(fieldsArrayString, " could not find a Provider higher up in the tree."));
            }
        }, [
            fields,
            store
        ]);
        var data = useSyncExternalStore(function(callback) {
            if (store) {
                return store.subscribe(callback, fields);
            }
            return function() {};
        }, function() {
            return store ? store.get() : initialStore;
        }, function() {
            return store ? store.get() : initialStore;
        });
        return [
            data,
            store ? store.set : function() {}
        ];
    };
    var StoreMetadataContext = /*#__PURE__*/ createContext(undefined);
    var Provider = function(param) {
        var children = param.children, value = param.value;
        return /*#__PURE__*/ _jsx(StoreMetadataContext.Provider, {
            value: useStoreMetadata(value ? _object_spread({}, initialStore, typeof value === "function" ? value(initialStore) : value) : _object_spread({}, initialStore)),
            children: children
        });
    };
    return {
        Provider: Provider,
        useStore: useStore
    };
};
